import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { ImpersonationBanner } from './ImpersonationBanner';
import type { ReactNode } from 'react';

const baseNavItems = [
  { label: 'Dashboard', path: '/dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { label: 'Workspaces', path: '/workspaces', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
  { label: 'Team', path: '/team', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { label: 'Billing', path: '/billing', icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z' },
];

const adminNavItems = [
  { label: 'Settings', path: '/settings', icon: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z' },
];

const superAdminNavItems = [
  { label: 'Accounts', path: '/accounts', icon: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z' },
];

function NavIcon({ d, className = 'w-5 h-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

export function DashboardLayout({ children }: { children: ReactNode }) {
  const { user, account, logout, isImpersonating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Banner height used to offset fixed chrome (sidebar, mobile header, drawer)
  // and the in-flow spacer so nothing is covered by the banner.
  const bannerTop = isImpersonating ? 'top-11' : 'top-0';

  // Sidebar collapse state (desktop)
  const [collapsed, setCollapsed] = useState(false);
  // Mobile menu open state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'account_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  // Super admin view: account-centric only (Dashboard + Accounts + Settings).
  // They don't manage workspaces, team, or billing directly — they drill into an
  // account to manage those on the account admin's behalf (via impersonation).
  const navItems = isSuperAdmin
    ? [
        baseNavItems[0], // Dashboard
        ...superAdminNavItems, // Accounts
        ...adminNavItems, // Settings
      ]
    : [
        ...baseNavItems,
        ...(isAdmin ? adminNavItems : []),
      ];

  const sidebarContent = (mobile: boolean) => (
    <>
      {/* Logo + Collapse/Close */}
      <div className="p-4 border-b border-navy-700 flex items-center justify-between">
        {collapsed && !mobile ? (
          <Link to="/dashboard" className="mx-auto">
            <img src="/logo-icon-small.png" alt="RankHelm" className="w-8 h-8" />
          </Link>
        ) : (
          <Link to="/dashboard">
            <img src="/logo-full.png" alt="RankHelm" className="h-8" />
          </Link>
        )}
        {mobile ? (
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 rounded-lg hover:bg-navy-800 text-slate-400 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-lg hover:bg-navy-800 text-slate-400 hover:text-white transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {collapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              )}
            </svg>
          </button>
        )}
      </div>

      {/* Account name + short display ID (Sprint 10.5C / ADR-022) */}
      {account && !collapsed && (
        <div className="px-4 pt-3">
          <p className="text-xs text-slate-500 truncate">{account.name}</p>
          {account.display_id && (
            <p className="text-[10px] font-mono text-slate-600 truncate mt-0.5">
              Acct ID# {account.display_id}
            </p>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1" aria-label="Main navigation">
        {navItems.map((item) => {
          const active = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed && !mobile ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? 'bg-helm-500/15 text-helm-400'
                  : 'text-slate-400 hover:text-white hover:bg-navy-800'
              } ${collapsed && !mobile ? 'justify-center' : ''}`}
            >
              <NavIcon d={item.icon} />
              {(!collapsed || mobile) && item.label}
            </Link>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-navy-700">
        <div
          className={`flex items-center gap-3 cursor-pointer rounded-lg p-2 hover:bg-navy-800 transition-colors ${collapsed && !mobile ? 'justify-center' : ''}`}
          onClick={() => { navigate('/profile'); if (mobile) setMobileOpen(false); }}
          title={collapsed && !mobile ? `${user?.name} — Profile` : undefined}
        >
          <div className="w-8 h-8 rounded-full bg-helm-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              user?.name?.charAt(0).toUpperCase()
            )}
          </div>
          {(!collapsed || mobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.role?.replace('_', ' ')}</p>
            </div>
          )}
        </div>
        {(!collapsed || mobile) && (
          <div className="flex items-center gap-3 mt-2 px-2">
            <button
              onClick={() => { navigate('/profile'); if (mobile) setMobileOpen(false); }}
              className="text-xs text-helm-400 hover:text-helm-300 transition-colors"
            >
              Profile
            </button>
            <span className="text-slate-600">|</span>
            <button
              onClick={() => logout()}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
            >
              Sign out
            </button>
          </div>
        )}
        {collapsed && !mobile && (
          <button
            onClick={() => logout()}
            title="Sign out"
            aria-label="Sign out"
            className="mt-2 w-full flex justify-center text-slate-500 hover:text-slate-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-navy-950">
      <ImpersonationBanner />
      {/* In-flow spacer so the entire page is pushed below the fixed banner
          rather than sitting underneath it. */}
      {isImpersonating && <div aria-hidden className="h-11" />}
      {/* Mobile Header */}
      <header className={`md:hidden fixed ${bannerTop} left-0 right-0 z-40 bg-navy-900 border-b border-navy-700 px-4 py-3 flex items-center justify-between`}>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-1.5 rounded-lg hover:bg-navy-800 text-slate-400 hover:text-white transition-colors"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <Link to="/dashboard">
          <img src="/logo-full.png" alt="RankHelm" className="h-7" />
        </Link>
        <div className="w-9" />
      </header>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        className={`md:hidden fixed ${bannerTop} left-0 bottom-0 z-50 w-72 bg-navy-900 border-r border-navy-700 flex flex-col transform transition-transform duration-200 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent(true)}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex fixed ${bannerTop} left-0 bottom-0 z-30 bg-navy-900 border-r border-navy-700 flex-col transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {sidebarContent(false)}
      </aside>

      {/* Main Content */}
      <main
        className={`transition-all duration-200 ${
          collapsed ? 'md:ml-16' : 'md:ml-64'
        } pt-14 md:pt-0`}
      >
        <div className="max-w-6xl mx-auto p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
